import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { HelpStateItem } from '../interfaces';
import { useAToast } from '@Atoms';
import { useSessionStore } from '@Stores';
import { findHelpStateItem, removeHelpStateItem, replaceOrAppendHelpStateItem } from '../helpers';
import { RemoveHelpStateService, SaveHelpStateService } from '../services';

export default function useHelpState() {
  const { userProfile } = storeToRefs(useSessionStore());

  const { notify, TOAST_TYPES } = useAToast();

  const TOAST_ERROR = { title: 'helpTips.toasts.error.title', type: TOAST_TYPES.ERROR };

  const isSaving = ref(false);

  const helpState = computed(() => (userProfile.value?.help_state || []) as HelpStateItem[]);

  // HELPERS
  function isAlreadySaved(item: HelpStateItem) {
    const savedItem = findHelpStateItem(helpState.value, item.type, item.key, item.action);

    return !!savedItem && savedItem.seen_until === item.seen_until;
  }

  // NOTE :: An AnimalsFirst admin who is not logged in as a shelter has no shelter. The api answers
  // 401 to them, and the app treats a 401 as an expired session and logs out. So their choice is
  // kept on screen only, and nothing is sent.
  function isLocalOnlySession() {
    return !userProfile.value?.shelter_id;
  }

  function setHelpState(newHelpState: HelpStateItem[]) {
    if (!userProfile.value) return;

    userProfile.value = { ...userProfile.value, help_state: newHelpState };
  }

  // EVENTS
  function saveHelpState(item: HelpStateItem) {
    if (isAlreadySaved(item)) return Promise.resolve(true);

    const previousHelpState = helpState.value;

    setHelpState(replaceOrAppendHelpStateItem(previousHelpState, item));

    if (isLocalOnlySession()) return Promise.resolve(true);

    isSaving.value = true;

    return SaveHelpStateService(item)
      .then(({ data }) => {
        setHelpState(data.help_state);

        return true;
      })
      .catch(() => {
        setHelpState(previousHelpState);
        notify(TOAST_ERROR);

        return false;
      })
      .finally(() => (isSaving.value = false));
  }

  function removeHelpState(item: HelpStateItem) {
    if (!findHelpStateItem(helpState.value, item.type, item.key, item.action)) return Promise.resolve(true);

    const previousHelpState = helpState.value;

    setHelpState(removeHelpStateItem(previousHelpState, item));

    if (isLocalOnlySession()) return Promise.resolve(true);

    isSaving.value = true;

    return RemoveHelpStateService(item)
      .then(({ data }) => {
        setHelpState(data.help_state);

        return true;
      })
      .catch(() => {
        setHelpState(previousHelpState);
        notify(TOAST_ERROR);

        return false;
      })
      .finally(() => (isSaving.value = false));
  }

  return { helpState, isSaving, removeHelpState, saveHelpState };
}
